import { useEffect, useRef, useCallback } from "react";
import { RoomEvent } from "livekit-client";

/**
 * Detects when the orchestrator stops sending heartbeat messages (crash/OOMKill).
 *
 * The orchestrator broadcasts a dedicated heartbeat event every ~10 seconds via
 * the LiveKit server API (participant=undefined). This hook listens specifically
 * for those heartbeat events and fires onTimeout if none arrive within the threshold.
 *
 * Only activates after the first heartbeat arrives to prevent false triggers
 * in the lobby/ready room before the orchestrator starts.
 *
 * Backgrounding (iPad lock/app switch) pauses JS timers while wall-clock time
 * runs on, so without care the first check after resume instantly reads as a
 * timeout and reports a healthy session as crashed. The watchdog therefore
 * skips judgement while hidden and, on return to visible, restarts the silence
 * clock — the session gets a full threshold to prove itself before any verdict.
 *
 * @param {Object} options
 * @param {import("livekit-client").Room} options.room - LiveKit room instance
 * @param {boolean} options.enabled - Set false during restart or after session end
 * @param {Function} options.onTimeout - Called once when silence exceeds threshold,
 *   with { secondsSinceResume } (null when the page was never backgrounded) so
 *   callers can tell wake-up-adjacent timeouts from steady-state ones in logs.
 * @param {Function} [options.onHeartbeatReceived] - Called each time a heartbeat arrives
 * @param {number} [options.timeoutSeconds=30] - Silence threshold in seconds
 */
export const useOrchestratorHeartbeat = ({
  room,
  enabled = true,
  onTimeout,
  onHeartbeatReceived,
  timeoutSeconds = 30,
}) => {
  const lastMessageTime = useRef(null);
  const hasReceivedFirstMessage = useRef(false);
  const hasFiredTimeout = useRef(false);
  const lastResumeTime = useRef(null);

  const resetHeartbeat = useCallback(() => {
    lastMessageTime.current = null;
    hasReceivedFirstMessage.current = false;
    hasFiredTimeout.current = false;
  }, []);

  useEffect(() => {
    if (!room || !enabled) return;

    const onDataReceived = (payload, participant) => {
      if (participant) return;

      try {
        const decoded = new TextDecoder().decode(payload);
        const parsed = JSON.parse(decoded);
        if (parsed.event_type !== "heartbeat") return;
      } catch {
        return;
      }

      lastMessageTime.current = Date.now();
      hasReceivedFirstMessage.current = true;
      hasFiredTimeout.current = false;
      onHeartbeatReceived?.();
    };

    room.on(RoomEvent.DataReceived, onDataReceived);

    // Returning from background restarts the silence clock: the elapsed time
    // accumulated while suspended proves nothing about the orchestrator.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      lastResumeTime.current = Date.now();
      if (hasReceivedFirstMessage.current) {
        lastMessageTime.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = setInterval(() => {
      if (!hasReceivedFirstMessage.current) return;
      if (hasFiredTimeout.current) return;
      // No judging while hidden — data delivery may be paused along with us.
      if (document.visibilityState === "hidden") return;

      const elapsed = (Date.now() - lastMessageTime.current) / 1000;
      if (elapsed >= timeoutSeconds) {
        hasFiredTimeout.current = true;
        onTimeout?.({
          secondsSinceResume: lastResumeTime.current
            ? Math.round((Date.now() - lastResumeTime.current) / 1000)
            : null,
        });
      }
    }, 5000);

    return () => {
      room.off(RoomEvent.DataReceived, onDataReceived);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(intervalId);
    };
  }, [room, enabled, onTimeout, onHeartbeatReceived, timeoutSeconds]);

  return { resetHeartbeat };
};
